import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, setToken, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import PublicHeader from "@/components/public/PublicHeader";

const BUSINESS_TYPES = ["Retail", "Contractor", "Interior", "Painting", "Carpenter", "Furniture", "Electrical", "Plumbing", "Service Business", "Trader", "Wholesale", "Other"];
const STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Chandigarh","Jammu and Kashmir","Ladakh","Puducherry"];

export default function Signup() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", password: "",
    business_name: "", business_type: "Retail",
    gst_registered: false, gstin: "", state: "Maharashtra",
    accepted_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [justSignedUp, setJustSignedUp] = useState(false);

  if (user && !justSignedUp) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accepted_terms) return toast.error("Please accept the Terms & Privacy Policy");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", form);
      setToken(data.token);
      setJustSignedUp(true);
      await refresh();
      toast.success("Welcome to TheBillPro! Let's set up your business.");
      nav("/onboarding", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-2 gap-10 items-start">
        <div className="hidden md:block">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-3">
            <Sparkles size={12} /> 14-DAY FREE TRIAL
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Start billing smarter today</h1>
          <p className="mt-3 text-muted-foreground">Create your TheBillPro account in under a minute. No credit card required.</p>
          <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
            <li>✓ Full access to invoicing, quotations, customers</li>
            <li>✓ GST-ready tax invoices with PDF & WhatsApp share</li>
            <li>✓ Cancel anytime — your data is always safe</li>
          </ul>
          <div className="mt-8 p-4 rounded-lg bg-muted/40 border text-xs text-muted-foreground">
            Already have an account? <Link to="/login" className="text-primary font-medium">Sign in</Link>
          </div>
        </div>

        <Card className="p-6 sm:p-8">
          <div className="font-heading font-bold text-2xl">Create your TheBillPro account</div>
          <p className="text-sm text-muted-foreground mt-1">Free 14-day trial · ₹99/month after that</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Full Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="signup-name" required /></div>
              <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="signup-mobile" placeholder="+91 98765 43210" /></div>
              <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email" required /></div>
              <div><Label>Password *</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="signup-password" required minLength={6} /></div>
            </div>
            <div className="pt-2 border-t">
              <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3 mt-2">Business Details</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Business Name *</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="signup-business-name" required /></div>
                <div>
                  <Label>Business Type</Label>
                  <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} data-testid="signup-business-type">
                    {BUSINESS_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>State</Label>
                  <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="signup-state">
                    {STATES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.gst_registered} onCheckedChange={(v) => setForm({ ...form, gst_registered: !!v })} data-testid="signup-gst-registered" />
                    GST Registered?
                  </label>
                </div>
                {form.gst_registered && (
                  <div className="sm:col-span-2"><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} data-testid="signup-gstin" placeholder="27AAACU1234N1ZX" /></div>
                )}
              </div>
            </div>
            <label className="flex items-start gap-2 text-xs">
              <Checkbox checked={form.accepted_terms} onCheckedChange={(v) => setForm({ ...form, accepted_terms: !!v })} data-testid="signup-terms" className="mt-0.5" />
              <span>I agree to the <Link to="/terms" className="text-primary hover:underline">Terms &amp; Conditions</Link> and <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.</span>
            </label>
            <Button type="submit" className="w-full" size="lg" disabled={loading} data-testid="signup-submit">
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Create Account & Start Free Trial"}
            </Button>
            <div className="text-center text-xs text-muted-foreground">
              Already have an account? <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
            </div>
          </form>
        </Card>
      </section>
    </div>
  );
}
